import React from "react";
import { motion } from "framer-motion";
import { useForm, ValidationError } from "@formspree/react";
import { CheckCircle, Loader2, ArrowUpRight } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";

const FORMSPREE_RAW_ID = process.env.REACT_APP_FORMSPREE_FORM_ID || "";
const FORMSPREE_ID = FORMSPREE_RAW_ID || "placeholder";
const PEPDIRECT_URL = "https://pepdirect.co";

const subjectOptions = ["General", "Press", "Distribution enquiry", "Other"];

const inputStyle = {
  width: "100%",
  padding: "12px 16px",
  border: "1px solid rgba(255,255,255,0.18)",
  background: "#12204A",
  color: "#FFFFFF",
  fontSize: 14,
  outline: "none",
  transition: "border-color 0.2s",
};

const inputFocusHandler = (e) => (e.target.style.borderColor = "#8FA8E8");
const inputBlurHandler = (e) =>
  (e.target.style.borderColor = "rgba(255,255,255,0.18)");

export default function Contact() {
  const [state, handleSubmit] = useForm(FORMSPREE_ID);

  usePageMeta({
    title: "Contact · Trenta Medical",
    description:
      "General, press, and partnership enquiries for Trenta Medical. Purchasing enquiries are handled exclusively by PepDirect.",
    canonical: "https://trentamedical.com/contact",
  });

  return (
    <div style={{ background: "#1A2B5E", minHeight: "60vh" }} data-testid="contact-page">
      <section className="py-14 sm:py-18 lg:py-24">
        <div className="container mx-auto px-5 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-16">
            <motion.div
              className="lg:col-span-2"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="label-mono mb-3" style={{ color: "#8FA8E8" }}>
                § Contact
              </p>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-4 text-white">
                Contact Trenta Medical.
              </h1>
              <p
                className="text-sm sm:text-base leading-relaxed mb-8 max-w-xl"
                style={{ color: "rgba(255,255,255,0.7)" }}
              >
                For media, distribution, or general questions about Trenta
                Medical. Our team typically responds within 48 hours.
              </p>

              {state.succeeded ? (
                <div
                  className="p-8 sm:p-10 text-center"
                  style={{
                    background: "#12204A",
                    border: "1px solid rgba(255,255,255,0.14)",
                  }}
                  data-testid="form-success"
                >
                  <CheckCircle
                    size={44}
                    className="mx-auto mb-5"
                    style={{ color: "#4ade80" }}
                  />
                  <h3 className="text-xl font-bold mb-3 text-white">
                    Enquiry Received
                  </h3>
                  <p
                    className="text-sm sm:text-base leading-relaxed"
                    style={{ color: "rgba(255,255,255,0.7)" }}
                  >
                    Thank you. Our team will review your message and respond
                    within 48 hours.
                  </p>
                </div>
              ) : (
                <>
                  {!FORMSPREE_RAW_ID && (
                    <div
                      className="mb-6 p-4 text-sm"
                      style={{
                        background: "rgba(255,255,255,0.06)",
                        border: "1px solid rgba(255,255,255,0.18)",
                        color: "rgba(255,255,255,0.75)",
                      }}
                    >
                      <strong>Setup required:</strong> Add your Formspree form
                      ID to <code>REACT_APP_FORMSPREE_FORM_ID</code> in the{" "}
                      <code>.env</code> file.
                    </div>
                  )}

                  <form
                    onSubmit={handleSubmit}
                    className="space-y-6"
                    data-testid="contact-form"
                  >
                    <div>
                      <label
                        htmlFor="contact"
                        className="block text-sm font-semibold mb-2 text-white"
                      >
                        Telegram username or email{" "}
                        <span style={{ color: "#f87171" }}>*</span>
                      </label>
                      <input
                        id="contact"
                        type="text"
                        name="contact"
                        required
                        placeholder="@yourhandle or you@research.org"
                        style={{ ...inputStyle }}
                        onFocus={inputFocusHandler}
                        onBlur={inputBlurHandler}
                        data-testid="input-contact"
                      />
                      <ValidationError
                        field="contact"
                        prefix="Contact"
                        errors={state.errors}
                        className="mt-1 text-xs"
                        style={{ color: "#f87171" }}
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="subject"
                        className="block text-sm font-semibold mb-2 text-white"
                      >
                        Subject <span style={{ color: "#f87171" }}>*</span>
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        required
                        style={{ ...inputStyle, cursor: "pointer" }}
                        onFocus={inputFocusHandler}
                        onBlur={inputBlurHandler}
                        data-testid="select-subject"
                        defaultValue=""
                      >
                        <option value="" disabled style={{ background: "#12204A" }}>
                          Select a subject...
                        </option>
                        {subjectOptions.map((s) => (
                          <option key={s} value={s} style={{ background: "#12204A" }}>
                            {s}
                          </option>
                        ))}
                      </select>
                      <ValidationError
                        field="subject"
                        prefix="Subject"
                        errors={state.errors}
                        className="mt-1 text-xs"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="message"
                        className="block text-sm font-semibold mb-2 text-white"
                      >
                        Message <span style={{ color: "#f87171" }}>*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={5}
                        required
                        placeholder="Tell us how we can help."
                        style={{ ...inputStyle, resize: "none" }}
                        onFocus={inputFocusHandler}
                        onBlur={inputBlurHandler}
                        data-testid="textarea-message"
                      />
                      <ValidationError
                        field="message"
                        prefix="Message"
                        errors={state.errors}
                        className="mt-1 text-xs"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={state.submitting}
                      className="btn-white w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3.5 text-sm font-bold tracking-wide"
                      data-testid="submit-enquiry-btn"
                    >
                      {state.submitting ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          Sending...
                        </>
                      ) : (
                        "Send enquiry →"
                      )}
                    </button>
                  </form>
                </>
              )}

              <div
                className="mt-8 pt-6 text-sm leading-relaxed"
                style={{
                  borderTop: "1px solid rgba(255,255,255,0.14)",
                  color: "rgba(255,255,255,0.7)",
                }}
                data-testid="purchasing-note"
              >
                Purchasing enquiries are handled by PepDirect —{" "}
                <a
                  href={PEPDIRECT_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold transition-colors duration-200 hover:underline"
                  style={{ color: "#8FA8E8" }}
                  data-testid="purchasing-pepdirect-link"
                >
                  pepdirect.co
                  <ArrowUpRight size={12} className="inline ml-0.5 -mt-0.5" />
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="space-y-5 lg:pt-28"
            >
              <div
                className="p-7"
                style={{
                  background: "#12204A",
                  border: "1px solid rgba(255,255,255,0.14)",
                }}
                data-testid="contact-info-box"
              >
                <p className="label-mono mb-4" style={{ color: "#8FA8E8" }}>
                  § Before You Write
                </p>
                <div
                  className="space-y-3 text-sm leading-relaxed"
                  style={{ color: "rgba(255,255,255,0.6)" }}
                >
                  <p>
                    This form is for general, press, and partnership enquiries
                    only.
                  </p>
                  <p>
                    For orders, certificates, and product verification, please
                    use PepDirect directly.
                  </p>
                  <p>We aim to respond to all enquiries within 48 hours.</p>
                </div>
              </div>

              <div
                className="p-5"
                style={{
                  border: "1px solid rgba(255,255,255,0.18)",
                  background: "rgba(255,255,255,0.06)",
                }}
              >
                <p
                  className="text-xs leading-relaxed font-medium"
                  style={{ color: "rgba(255,255,255,0.7)" }}
                >
                  All compounds are intended exclusively for laboratory research
                  use. Not for human or veterinary consumption.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
